import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/utils";
import { Instagram, Mail, MapPin, ArrowRight, Phone } from "lucide-react";
import { useContent } from "../lib/content";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const logoImage = useContent("branding.logo_image", "");
  const tagline = useContent("branding.tagline", "I do not just take photos. I preserve souls.");
  const fEmail = useContent("contact.email", "info@cowrieking.com");
  const fPhone = useContent("contact.phone", "541-678-0035");
  const fInsta = useContent("contact.instagram1", "@cowrieking");
  const fLocation = useContent("contact.location", "Portland, Oregon · USA");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Welcome. You are now part of the journey.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0a0908] border-t border-white/5 mt-32"
    >
      {/* Newsletter band */}
      <div className="relative border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-6">
              The Cowrieking Letter
            </p>
            <h3 className="font-serif text-4xl sm:text-5xl text-[#f4f0ec] leading-tight">
              Receive dispatches from the field — quiet, occasional, always handwritten.
            </h3>
          </div>
          <div className="flex items-end">
            <form
              onSubmit={submit}
              data-testid="newsletter-form"
              className="w-full"
            >
              <div className="flex border-b border-white/15 focus-within:border-[#e6c155] transition-colors">
                <label htmlFor="newsletter-email" className="sr-only">Email address for newsletter</label>
                <input
                  id="newsletter-email"
                  type="email"
                  data-testid="newsletter-email-input"
                  placeholder="your@email.com"
                  aria-label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 bg-transparent py-4 text-[#f4f0ec] placeholder:text-[#9c958e] focus:outline-none font-sans"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={submitting}
                  aria-label="Subscribe to newsletter"
                  className="px-4 text-[#e6c155] hover:text-[#f4f0ec] transition-colors"
                >
                  <ArrowRight size={20} aria-hidden="true" />
                </button>
              </div>
              <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{position:'absolute',left:'-9999px',width:'1px',height:'1px',opacity:0}} aria-hidden="true" />
              <p className="text-xs text-[#78716c] mt-3 font-sans">
                Field notes, new collections, stories from Ada Foah. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              {logoImage ? (
                <img
                  src={logoImage}
                  alt="Cowrieking logo"
                  data-testid="footer-logo-image"
                  loading="lazy"
                  decoding="async"
                  className="w-12 h-12 object-cover rounded-sm"
                />
              ) : (
                <span className="w-10 h-10 border border-[#d4af37] flex items-center justify-center font-serif text-[#d4af37] text-xl italic">
                  C
                </span>
              )}
              <div>
                <div className="font-serif text-xl text-[#f4f0ec]">Cowrieking</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37] mt-1">
                  Mershack Kabu Aklie
                </div>
              </div>
            </Link>
            <p className="text-[#a8a29e] max-w-md leading-relaxed font-sans">
              Photographer, storyteller, humanitarian. Documenting humanity, preserving African identity, and turning images into impact through Eyes of Hope Foundation and Adanobi Studios.
            </p>
            <div className="flex gap-3 mt-8">
              <a
                href={`https://instagram.com/${fInsta.replace("@", "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="w-11 h-11 border border-white/15 hover:border-[#e6c155] hover:text-[#e6c155] flex items-center justify-center text-[#c4bdb5] transition-colors"
                aria-label="Follow Cowrieking on Instagram (opens in new tab)"
              >
                <Instagram size={18} aria-hidden="true" />
              </a>
              <a
                href={`mailto:${fEmail}`}
                data-testid="footer-email-link"
                className="w-11 h-11 border border-white/15 hover:border-[#e6c155] hover:text-[#e6c155] flex items-center justify-center text-[#c4bdb5] transition-colors"
                aria-label="Email Cowrieking studio"
              >
                <Mail size={18} aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#d4af37] mb-5">
              Explore
            </p>
            <ul className="space-y-3">
              {[
                ["/about", "About"],
                ["/gallery", "Photography"],
                ["/journal", "Journal"],
                ["/store", "Fine Art Store"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[#a8a29e] hover:text-[#d4af37] text-sm transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#d4af37] mb-5">
              Engage
            </p>
            <ul className="space-y-3">
              {[
                ["/eyes-of-hope", "Eyes of Hope"],
                ["/adanobi", "Adanobi Studios"],
                ["/voices", "Voices of Humanity"],
                ["/philosophy", "Philosophy"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[#a8a29e] hover:text-[#d4af37] text-sm transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#d4af37] mb-5">
              Studio
            </p>
            <p className="text-sm text-[#a8a29e] flex items-start gap-2 mb-3">
              <MapPin size={14} className="mt-1 flex-shrink-0" />
              <span>{fLocation}</span>
            </p>
            <p className="text-sm text-[#a8a29e] flex items-start gap-2 mb-3">
              <Mail size={14} className="mt-1 flex-shrink-0" />
              <a
                href={`mailto:${fEmail}`}
                className="hover:text-[#d4af37] transition-colors"
              >
                {fEmail}
              </a>
            </p>
            <p className="text-sm text-[#a8a29e] flex items-start gap-2">
              <Phone size={14} className="mt-1 flex-shrink-0" />
              <a
                href={`tel:${fPhone.replace(/[^+\d]/g, "")}`}
                className="hover:text-[#d4af37] transition-colors"
              >
                {fPhone}
              </a>
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-[#78716c] font-mono">
            © {new Date().getFullYear()} Mershack Kabu Aklie · Cowrieking. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-xs text-[#78716c] font-mono">
            <Link to="/transparency" className="hover:text-[#d4af37]">Transparency</Link>
            <Link to="/press" data-testid="footer-press-link" className="hover:text-[#d4af37]">Press</Link>
            <Link to="/volunteer" data-testid="footer-volunteer-link" className="hover:text-[#d4af37]">Volunteer</Link>
            <Link to="/privacy" className="hover:text-[#d4af37]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#d4af37]">Terms</Link>
          </div>
          <p data-testid="footer-tagline" className="text-xs text-[#78716c] font-serif italic">
            "{tagline}"
          </p>
        </div>
      </div>
    </footer>
  );
}
